import Save from '@mui/icons-material/Save';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import {
  Account_Create,
  Account_Invite,
  Account_Update,
  MatchKeyMatchableType,
  ModelError_Item,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { ShakyErrorIndicator } from '~components/ErrorIndicators/ShakyErrorIndicator';
import MatchKeysSection from '~components/MatchKeys/MatchKeysSection';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { Account } from '~hooks/useAccount/models';
import { CompanyShare } from '~hooks/useCompanyShares/models';
import useReportSaving from '~hooks/useReportSaving/useReportSaving';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import ConnectedAccountContactPanel, {
  ConnectedAccountContactPanelHandler,
} from '../ConnectedAccountContactPanel/ConnectedAccountContactPanel';
import NotesPanel, { NotesPanelHandler } from '../NotesPanel/NotesPanel';
import TruckCapacityPanel, {
  TruckCapacityPanelHandler,
} from '../TruckCapacityPanel/TruckCapacityPanel';
import AccountConfigurationPanel, {
  AccountConfigurationPanelHandler,
} from './AccountConfigurationPanel/AccountConfigurationPanel';
import AccountContactPanel, {
  AccountContactPanelHandler,
} from './AccountContactPanel/AccountContactPanel';
import AccountDetailsPanel, {
  AccountDetailsPanelHandler,
} from './AccountDetailsPanel/AccountDetailsPanel';
import {
  collectConfigurationForNewConnectedAccount,
  collectConfigurationForNewNonConnectedAccount,
  collectContactsForNewConnectedAccount,
  collectContactsForNewNonConnectedAccount,
  collectDetailsForNewConnectedAccount,
  collectDetailsForNewNonConnectedAccount,
  collectNotesForConnectedAccount,
  collectNotesForNonConnectedAccount,
  createCompanyShares,
  createConnectedAccount,
  createNonConnectedAccount,
} from './create-account-helpers';
import {
  collectConfigurationToUpdateConnectedAccount,
  collectContactsToUpdateConnectedAccount,
  collectContactsToUpdateNonConnectedAccount,
  collectDetailsToUpdateAccount,
  collectNotesToUpdateAccount,
  collectScalarTruckCountToUpdateAccount,
  diffCompanyShares,
  updateAccount,
  updateCompanyShares,
} from './update-account-helpers';

enum SaveAccountStep {
  SAVING_ACCOUNT = 'saving-account',
  SAVING_COMPANY_SHARES = 'saving-company-shares',
}

interface CustomerAccountFormProps {
  account?: Account;
  isOpen: boolean;
  onAccountCreated?: (createdCompanyShares?: CompanyShare[]) => void;
  onAccountUpdated?: (
    createdCompanyShares?: CompanyShare[],
    deletedCompanyShares?: string[],
  ) => void;
  onClose: () => void;
  onSavingChange?: (isSaving: boolean) => void;
}

export default function CustomerAccountForm({
  account,
  isOpen,
  onAccountCreated,
  onAccountUpdated,
  onClose,
  onSavingChange,
}: CustomerAccountFormProps) {
  const { userStore, toasterStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const accountDetailsPanelRef = useRef<AccountDetailsPanelHandler>(null);
  const accountContactPanelRef = useRef<AccountContactPanelHandler>(null);
  const connectedAccountContactPanelRef =
    useRef<ConnectedAccountContactPanelHandler>(null);
  const accountConfigurationPanelRef = useRef<AccountConfigurationPanelHandler>(null);
  const notesPanelRef = useRef<NotesPanelHandler>(null);
  const truckCapacityPanelRef = useRef<TruckCapacityPanelHandler>(null);

  const [saveAccountStep, setSaveAccountStep] = useState<SaveAccountStep>();

  // In Edit mode reflect the saved value; new accounts default to portal access on.
  const [isConnectedAccount, setIsConnectedAccount] = useState(
    account ? Boolean(account.connectedCompany) : true,
  );
  const [apiResponseErrors, setApiResponseErrors] = useState<ModelError_Item[]>();

  const isEditing = Boolean(account?.id);
  const shouldShowAccountConfigurationPanel = isEditing ? isConnectedAccount : true;
  const isSubmitting = Boolean(saveAccountStep);
  useReportSaving(isSubmitting, onSavingChange);

  const saveButtonTextBySaveAccountStep: Record<SaveAccountStep, string> = {
    [SaveAccountStep.SAVING_ACCOUNT]: t('account.form.saving_account'),
    [SaveAccountStep.SAVING_COMPANY_SHARES]: t('account.form.saving_company_shares'),
  };

  const handleIsConnectedAccountChange = useCallback((isConnectedAccount: boolean) => {
    setIsConnectedAccount(isConnectedAccount);
  }, []);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      event.stopPropagation();

      let newAccount: Account | undefined;
      let updatedAccount: Account | undefined;

      try {
        const accountDetails = isEditing
          ? await collectDetailsToUpdateAccount(accountDetailsPanelRef)
          : isConnectedAccount
            ? await collectDetailsForNewConnectedAccount(accountDetailsPanelRef)
            : await collectDetailsForNewNonConnectedAccount(accountDetailsPanelRef);

        const accountContact = isEditing
          ? isConnectedAccount
            ? await collectContactsToUpdateConnectedAccount(
                connectedAccountContactPanelRef,
              )
            : await collectContactsToUpdateNonConnectedAccount(accountContactPanelRef)
          : isConnectedAccount
            ? await collectContactsForNewConnectedAccount(connectedAccountContactPanelRef)
            : await collectContactsForNewNonConnectedAccount(accountContactPanelRef);

        const accountConfiguration = isEditing
          ? isConnectedAccount
            ? await collectConfigurationToUpdateConnectedAccount(
                accountConfigurationPanelRef,
              )
            : {}
          : isConnectedAccount
            ? await collectConfigurationForNewConnectedAccount(
                accountConfigurationPanelRef,
              )
            : await collectConfigurationForNewNonConnectedAccount(
                accountConfigurationPanelRef,
              );

        const notes = isEditing
          ? await collectNotesToUpdateAccount(notesPanelRef)
          : isConnectedAccount
            ? await collectNotesForConnectedAccount(notesPanelRef)
            : await collectNotesForNonConnectedAccount(notesPanelRef);

        const truckCapacity = isEditing
          ? await collectScalarTruckCountToUpdateAccount(
              truckCapacityPanelRef,
              isConnectedAccount,
            )
          : {};

        if (
          accountDetails?.values &&
          accountContact &&
          accountConfiguration &&
          notes &&
          truckCapacity
        ) {
          setApiResponseErrors(undefined);

          if (isEditing) {
            if (!account?.id) {
              return;
            }

            setSaveAccountStep(SaveAccountStep.SAVING_ACCOUNT);

            const values = {
              ...accountDetails.values,
              ...accountContact,
              ...accountConfiguration,
              ...notes,
              ...truckCapacity,
            } as Account_Update;

            const response = await updateAccount(
              values,
              account.id,
              accountDetails.companyId || companyId,
            );
            updatedAccount = response.account;

            setApiResponseErrors(response.errors);

            if (response.errors) {
              setSaveAccountStep(undefined);
            }

            if (updatedAccount) {
              toasterStore.push(
                alert(
                  t('account.account_updated', { name: updatedAccount.name }),
                  AlertTypes.success,
                ),
              );
            }
          } else {
            setSaveAccountStep(SaveAccountStep.SAVING_ACCOUNT);

            let errors: ModelError_Item[] | undefined;

            if (isConnectedAccount) {
              const values = {
                ...accountDetails.values,
                ...accountContact,
                ...accountConfiguration,
                ...notes,
              } as Account_Invite;

              const response = await createConnectedAccount(
                {
                  name: values.name,
                  contact: values.contact,
                  address: values.address,
                  account_types: values.account_types,
                  department_id: values.department_id,
                  notes: values.notes,
                  external_id: values.external_id,
                  auto_accept_orders: values.auto_accept_orders,
                  send_invite: values.send_invite,
                  company_type: values.company_type,
                  show_user_phone_number: values.show_user_phone_number,
                  label_ids: values.label_ids,
                },
                accountDetails.companyId || companyId,
              );
              newAccount = response.account;
              errors = response.errors;
            } else {
              const values = {
                ...accountDetails.values,
                ...accountContact,
                ...accountConfiguration,
                ...notes,
              } as Account_Create;

              const response = await createNonConnectedAccount(
                {
                  name: values.name,
                  department_id: values.department_id,
                  primary_contact: values.primary_contact,
                  billing_contact: values.billing_contact,
                  address: values.address,
                  billing_address: values.billing_address,
                  notes: values.notes,
                  account_types: values.account_types,
                  external_id: values.external_id,
                  accounting_id: values.accounting_id,
                  auto_accept_orders: false,
                  label_ids: values.label_ids,
                },
                accountDetails.companyId || companyId,
              );
              newAccount = response.account;
              errors = response.errors;
            }

            setApiResponseErrors(errors);

            if (errors) {
              setSaveAccountStep(undefined);
            }

            if (newAccount) {
              toasterStore.push(
                alert(
                  t('account.account_created', { name: newAccount.name }),
                  AlertTypes.success,
                ),
              );
            }
          }
        }
      } catch (error) {
        setSaveAccountStep(undefined);

        connection.handleRequestError(
          error,
          t('error_messages.account.failed_to_create'),
        );
      }

      if (newAccount) {
        const data = await accountConfigurationPanelRef.current?.onSubmit();
        const hasSharedDrivers = data?.drivers?.length;
        const hasSharedEquipment = data?.equipment?.length;

        if (hasSharedDrivers || hasSharedEquipment) {
          setSaveAccountStep(SaveAccountStep.SAVING_COMPANY_SHARES);

          try {
            const companyShares = await createCompanyShares(
              accountConfigurationPanelRef,
              newAccount,
            );

            setSaveAccountStep(undefined);
            onAccountCreated?.(companyShares);
            onClose();
          } catch (error) {
            setSaveAccountStep(undefined);

            connection.handleRequestError(
              error,
              t('error_messages.company_shares.failed_to_share_driver_or_equipment'),
            );
          }
        } else {
          setSaveAccountStep(undefined);
          onAccountCreated?.();
          onClose();
        }
      } else if (updatedAccount) {
        const companySharesDiff = await diffCompanyShares(
          accountConfigurationPanelRef,
          updatedAccount,
        );

        const hasSharedDriversToManage =
          companySharesDiff?.drivers.toCreate.length ||
          companySharesDiff?.drivers.toDelete.length;
        const hasSharedEquipmentToManage =
          companySharesDiff?.equipment.toCreate.length ||
          companySharesDiff?.equipment.toDelete.length;

        if (hasSharedDriversToManage || hasSharedEquipmentToManage) {
          setSaveAccountStep(SaveAccountStep.SAVING_COMPANY_SHARES);

          try {
            const companyShares = await updateCompanyShares(
              companySharesDiff.drivers,
              companySharesDiff.equipment,
              updatedAccount,
            );

            const createdCompanyShares = (companyShares?.drivers.created ?? []).concat(
              companyShares?.equipment.created ?? [],
            );
            const deletedCompanyShares = (companyShares?.drivers.deleted ?? []).concat(
              companyShares?.equipment.deleted ?? [],
            );

            setSaveAccountStep(undefined);
            onAccountUpdated?.(createdCompanyShares, deletedCompanyShares);
            onClose();
          } catch (error) {
            setSaveAccountStep(undefined);

            connection.handleRequestError(
              error,
              t('error_messages.company_shares.failed_to_unshare_driver_or_equipment'),
            );
          }
        } else {
          setSaveAccountStep(undefined);
          onAccountUpdated?.();
          onClose();
        }
      }
    },
    [
      account?.id,
      companyId,
      isConnectedAccount,
      isEditing,
      onAccountCreated,
      onAccountUpdated,
      onClose,
    ],
  );

  useEffect(() => {
    return () => {
      setApiResponseErrors(undefined);
      setSaveAccountStep(undefined);
    };
  }, [isOpen]);

  return (
    <Dialog
      isOpen={isOpen || false}
      maxWidth="lg"
      preventCloseOnBackdropClick
      preventCloseOnEscapeKeyDown
    >
      <DialogHeader onClose={onClose}>
        {account?.id ? t('account.update_account') : t('account.create_account')}
      </DialogHeader>

      <DialogContent sx={(theme) => ({ bgcolor: theme.semantic.surfaceSunken })}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6 }}>
            <Grid container>
              <Grid size={{ xs: 12 }}>
                <Card sx={{ p: 2, mb: 2 }}>
                  <Typography variant="form-section-title" sx={{ mb: 2 }}>
                    {t('account.form.account_details')}
                  </Typography>

                  <AccountDetailsPanel
                    ref={accountDetailsPanelRef}
                    account={account}
                    apiResponseErrors={apiResponseErrors}
                    isConnectedAccount={isConnectedAccount}
                  />
                </Card>
              </Grid>

              {shouldShowAccountConfigurationPanel && (
                <Grid size={{ xs: 12 }}>
                  <Card sx={{ p: 2, mb: 2 }}>
                    <Typography variant="form-section-title" sx={{ mb: 2 }}>
                      {t('account.form.account_configuration')}
                    </Typography>

                    <AccountConfigurationPanel
                      ref={accountConfigurationPanelRef}
                      account={account}
                      onIsConnectedAccountChange={handleIsConnectedAccountChange}
                    />
                  </Card>
                </Grid>
              )}
            </Grid>
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Grid container>
              <Grid size={{ xs: 12 }}>
                <Card sx={{ p: 2, mb: 2, overflow: 'visible' }}>
                  <Typography
                    variant="form-section-title"
                    sx={isConnectedAccount ? {} : { mb: 2 }}
                  >
                    {t('account.form.contact_information')}
                  </Typography>

                  {isConnectedAccount ? (
                    <>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        {t('account.form.primary_account_contact_hint')}
                      </Typography>

                      <ConnectedAccountContactPanel
                        ref={connectedAccountContactPanelRef}
                        account={account}
                        apiResponseErrors={apiResponseErrors}
                      />
                    </>
                  ) : (
                    <AccountContactPanel ref={accountContactPanelRef} account={account} />
                  )}
                </Card>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Card sx={{ p: 2, mb: 2 }}>
                  <Typography variant="form-section-title">
                    {t('form_fields.notes')}
                  </Typography>

                  <NotesPanel ref={notesPanelRef} account={account} />
                </Card>
              </Grid>

              {isEditing && (
                <Grid size={{ xs: 12 }}>
                  <Card sx={{ p: 2, mb: 2 }}>
                    <Typography variant="form-section-title">
                      {t('form_fields.truck_capacity')}
                    </Typography>

                    <TruckCapacityPanel
                      ref={truckCapacityPanelRef}
                      account={account}
                      apiResponseErrors={apiResponseErrors}
                      companyId={companyId}
                      isConnectedAccount={isConnectedAccount}
                    />
                  </Card>
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <MatchKeysSection
                  companyId={companyId}
                  matchableId={account?.id}
                  matchableType={MatchKeyMatchableType.ACCOUNT}
                  recordName={account?.name ?? ''}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        {Boolean(apiResponseErrors?.length) && (
          <ShakyErrorIndicator
            message={t('administration.users.user_form.invalid_data_error_messages')}
          />
        )}

        <SmallButton
          color="secondary"
          disabled={isSubmitting}
          onClick={onClose}
          variant="outlined"
        >
          {t('actions.cancel')}
        </SmallButton>

        <SmallLoadingButton
          loading={isSubmitting}
          loadingPosition="start"
          onClick={handleSubmit}
          startIcon={isSubmitting ? <Save sx={{ color: 'transparent' }} /> : <></>}
          sx={isSubmitting ? {} : { '& .MuiButton-startIcon': { mx: 0 } }}
        >
          {saveAccountStep
            ? saveButtonTextBySaveAccountStep[saveAccountStep]
            : t('actions.save')}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
}
